import { useEffect, useState } from "react";

import { toast } from "react-toastify";

import Modal, { ModalProps } from "react-bootstrap/Modal";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

import { THEME } from "@/styles/theme";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import statesAndCities from "@/utils/static/states-and-cities.json";
import { LicenseEnum } from "@/utils/enums/LicenseEnum";
import { StatesAndCities } from "@/@types/statesAndCities";
import { Account } from "@/@types/app/Account";
import { Role } from "@/@types/app/Role";
import { FederalUnit } from "@/@types/app/FederalUnit";
import {
	CityEsusRoleEnum,
	CitySIntegradaRoleEnum,
	StateEsusRoleEnum,
} from "@/utils/enums/RoleEnum";
import { getPermittedFederalUnits } from "@/services/app/userService";
import { getActivatedCities, getNotLinkedCities } from "@/services/app/cityService";
import { getAllProfiles } from "@/services/app/profileService";
import { requestAccess } from "@/services/app/userService";
import { requestCityLicense } from "@/services/app/cityLicenseService";
import { Option } from "@/@types/generics/Option";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";
import { Module } from "@/@types/app/Module";

import { ProfileSelect } from "@/components/ProfileSelect";
import { ModuleSelect } from "../ModuleSelect";
import { SelectComponent } from "../SelectComponent";

type Props = ModalProps & {
	handleClose?: () => void;
};

export function ModalNewCityLicense({ handleClose, ...rest }: Props) {
	const { user } = useCurrentAccount();
	const { states, cities } = statesAndCities as StatesAndCities;
	const [confirmTerms, setConfirmTerms] = useState(false);
	const [activatedCities, setActivatedCities] = useState<Account[]>([]);
	const [selectedOptionProfile, setSelectedOptionProfile] = useState<Option<number>>();
	const [selectedOptionState, setSelectedOptionState] = useState<Option<string>>();
	const [selectedOptionCity, setSelectedOptionCity] = useState<Option<string>>();
	const [selectedOptionModule, setSelectedOptionModule] = useState<Option<number>>();
	const [selectedOptionAccessType, setSelectedOptionAccessType] = useState("city");
	const [isLoading, setIsLoading] = useState(false);
	const [permittedFederalUnits, setPermittedFederalUnits] = useState<FederalUnit[]>([]);
	const selectedModule = selectedOptionModule?.value
		? ModuleEnum[ModuleEnum[Number(selectedOptionModule?.value)] as keyof typeof ModuleEnum]
		: undefined;
	const [notLinkedCities, setNotLinkedCities] = useState<Account[]>([]);
	const [roles, setRoles] = useState<Role[]>([]);

	const modules: Module[] = [
		{ id: ModuleEnum.ESUS_APS, description: "Esus Aps" },
		{ id: ModuleEnum.SINTEGRADA, description: "Saúde Integrada" },
	];

	const filteredModules = modules.filter(
		(module) => selectedOptionAccessType !== "uf" || module.id !== ModuleEnum.SINTEGRADA
	);

	const filteredRoles = roles.filter((role) =>
		selectedOptionAccessType === "uf"
			? Object.values(StateEsusRoleEnum).find((stateRole) => String(role.id) === stateRole)
			: selectedModule === ModuleEnum.ESUS_APS
			? Object.values(CityEsusRoleEnum).find((roleObject) => String(role.id) === roleObject)
			: Object.values(CitySIntegradaRoleEnum).find(
					(roleObject) => String(role.id) === roleObject
			  )
	);

	async function handleFetchNotLinkedCities() {
		setNotLinkedCities(await getNotLinkedCities(selectedModule));
	}

	async function handleFetchProfiles() {
		setRoles(await getAllProfiles());
	}

	const selectedCityIsActive =
		activatedCities.filter(
			(cityActivated) => cityActivated.ibgeCode === selectedOptionCity?.value
		).length > 0;

	const selectStates: Option<string>[] = states
		.filter(
			(state) =>
				selectedOptionAccessType === "city" ||
				(selectedOptionAccessType === "uf" &&
					permittedFederalUnits.filter(
						(federalUnit) => federalUnit?.abbreviation !== state.abbreviation
					))
		)
		.map((state) => ({
			label: state.abbreviation,
			value: String(state.abbreviation),
		}));

	const isDisabledRegisterButton =
		isLoading ||
		!Boolean(
			selectedOptionCity &&
				selectedOptionCity.value &&
				selectedOptionProfile &&
				selectedOptionProfile.value &&
				((confirmTerms && !selectedCityIsActive) || selectedCityIsActive)
		);

	const selectCities = cities
		.filter(
			(city) =>
				city.microrregiao.mesorregiao.UF.sigla === selectedOptionState?.value &&
				(!activatedCities.find(
					(cityActivated) => cityActivated.ibgeCode === String(city.id)
				) ||
					notLinkedCities.find(
						(notLinkedAccount) => notLinkedAccount.ibgeCode === String(city.id)
					))
		)
		.map(
			(city) =>
				({
					label: city.nome,
					value: String(city.id),
				} as Option<string>)
		);

	async function handleFetchCitiesActivated() {
		setActivatedCities(await getActivatedCities());
	}

	async function handleFetchPermittedFederalUnits() {
		setPermittedFederalUnits(await getPermittedFederalUnits());
	}

	async function handleRegister() {
		if (!selectedModule || !selectedOptionState) return;
		setIsLoading(true);
		if (
			activatedCities.find(
				(cityActivated) => String(cityActivated.ibgeCode) === selectedOptionCity?.value
			) ||
			(selectedOptionState.value && !selectedOptionCity?.value)
		) {
			await handleRequestAccess(
				selectedModule,
				selectedOptionState.value,
				selectedOptionCity?.value,
				selectedOptionProfile?.value
			);
		} else if (selectedOptionCity?.value) {
			await handleRequestCityLicense(
				selectedModule,
				selectedOptionState.value,
				selectedOptionCity?.value,
				selectedOptionProfile?.value
			);
		}
		setIsLoading(false);
	}

	async function handleRequestAccess(
		module: ModuleEnum,
		uf: string,
		ibgeCode?: string,
		roleId?: number
	) {
		const isRequested = await requestAccess(module, uf, ibgeCode, roleId);
		if (isRequested) {
			toast.info(
				`Solicitação registrada. Entre em contato com o gestor para efetuar a ativação.`,
				{ bodyClassName: "fs-5" }
			);
			setTimeout(() => {
				handleClose && handleClose();
			}, 3000);
		} else {
			toast.error(`Erro ao solicitar acesso.`, { bodyClassName: "fs-5" });
		}
	}

	async function handleRequestCityLicense(
		module: ModuleEnum,
		uf: string,
		ibgeCode: string,
		roleId?: number
	) {
		if (!selectedOptionState?.value || !selectedOptionCity?.value || !selectedOptionCity?.label)
			return;
		const isRequested = await requestCityLicense({
			module,
			uf,
			ibgeCode,
			locale: selectedOptionCity?.label,
			identifier: user.identifier,
			licenseId:
				module === ModuleEnum.ESUS_APS
					? LicenseEnum.ESUS_TEMPORARY
					: LicenseEnum.SINTEGRADA_PERMANENTLY,
			roleId: roleId,
		});
		if (isRequested) {
			toast.success("A confirmação da liberação da licença, chegará no seu whatssap.", {
				bodyClassName: "fs-5",
			});
			await handleFetchCitiesActivated();
			setSelectedOptionCity({ value: "", label: "Selecione uma cidade" });
			setTimeout(() => {
				handleClose && handleClose();
			}, 3000);
		} else {
			toast.error("Falha na solicitação.");
		}
	}

	useEffect(() => {
		if (rest.show) {
			handleFetchPermittedFederalUnits();
			handleFetchCitiesActivated();
			handleFetchNotLinkedCities();
			handleFetchProfiles();
		}
		return () => {
			setSelectedOptionModule({} as Option<number>);
			setSelectedOptionCity({} as Option<string>);
			setSelectedOptionState({} as Option<string>);
			setSelectedOptionProfile({} as Option<number>);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rest.show]);

	return (
		<Modal size="lg" {...rest}>
			<Modal.Header
				closeVariant="white"
				closeButton
				style={{ backgroundColor: THEME.COLORS.INFO }}
			>
				<Modal.Title className="text-white">Solicitação de Acesso</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<section className="mb-5 ms-3 me-3 pe-10 ps-10">
					<Row className="mb-4">
						<Col>
							<label htmlFor="option" className="fs-6 mb-4">
								Formato de acesso
							</label>
							<div>
								<Form.Check
									inline
									label="Municipal"
									className="text-nowrap"
									name="option"
									type="radio"
									id="city-radio"
									onChange={() => {
										setSelectedOptionAccessType("city");
										setSelectedOptionModule({
											label: "Selecione um módulo",
										} as Option<number>);
										setSelectedOptionCity({} as Option<string>);
										setSelectedOptionProfile({} as Option<number>);
									}}
									checked={selectedOptionAccessType === "city"}
								/>
								<Form.Check
									inline
									label="Estadual"
									className="text-nowrap"
									name="option"
									type="radio"
									id="uf-radio"
									onChange={() => {
										setSelectedOptionAccessType("uf");
										setSelectedOptionModule({
											value: ModuleEnum.ESUS_APS,
											label:
												modules.find(
													(module) => module.id === ModuleEnum.ESUS_APS
												)?.description || "",
										} as Option<number>);
										setSelectedOptionCity({
											value: "",
											label: "Selecione uma cidade",
										});
										setSelectedOptionProfile({
											value: 5,
											label: "Administrador estadual",
										} as Option<number>);
									}}
									checked={selectedOptionAccessType === "uf"}
								/>
							</div>
						</Col>
					</Row>
					<Row>
						<Col>
							<ModuleSelect
								id="module-select"
								className="fs-6 mb-2"
								noDefaultOption={true}
								modules={filteredModules}
								value={selectedOptionModule}
								onChange={(newValue) => {
									setSelectedOptionModule(newValue as Option<number>);
									if (selectedOptionAccessType === "city") {
										setSelectedOptionAccessType("city");
										setSelectedOptionProfile({
											value: 2,
											label: "Administrador",
										} as Option<number>);
									} else {
										setSelectedOptionCity({
											value: "",
											label: "Selecione uma cidade",
										});
										setSelectedOptionProfile({
											value: 5,
											label: "Administrador estadual",
										} as Option<number>);
									}
								}}
								isDisabled={selectedOptionAccessType === "uf"}
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={2} md={2}>
							<label htmlFor="state">Estado</label>
							<SelectComponent
								onChange={(newValue) =>
									setSelectedOptionState(newValue as Option<string>)
								}
								defaultValue={{ value: "", label: "UF" }}
								noOptionsMessage={() => "Nenhum estado encontrado"}
								options={selectStates}
								value={selectedOptionState}
								className="fs-6 mb-2"
								name="state"
								id="state"
							/>
						</Col>
						<Col lg={10} md={10}>
							<label htmlFor="city">Cidade de vinculo profissional</label>
							<SelectComponent
								onChange={(newValue) =>
									setSelectedOptionCity(newValue as Option<string>)
								}
								defaultValue={{ value: "", label: "Selecione uma cidade" }}
								noOptionsMessage={() => "Nenhuma cidade encontrada"}
								options={selectCities}
								value={selectedOptionCity}
								className="mb-2"
								name="city"
								id="city"
								isDisabled={selectedOptionAccessType === "uf"}
							/>
						</Col>
					</Row>
					<Row>
						<Col>
							<ProfileSelect
								id="Profile-select"
								className="fs-6"
								noDefaultOption={true}
								roles={filteredRoles}
								value={selectedOptionProfile}
								onChange={(newValue) => {
									setSelectedOptionProfile(newValue as Option<number>);
								}}
								isDisabled={
									selectedOptionAccessType === "uf" ||
									!selectedOptionModule?.value
								}
							/>
						</Col>
					</Row>
					{!(selectedCityIsActive || selectedOptionAccessType === "uf") && (
						<div className="mt-3">
							<input
								className="form-check-input fs-9"
								type="checkbox"
								name="confirm-terms"
								checked={confirmTerms}
								onChange={() => setConfirmTerms(!confirmTerms)}
								disabled={selectedCityIsActive || selectedOptionAccessType === "uf"}
							/>
							<label className="mx-2">
								Confirmo que estou solicitando uma licença temporária.
							</label>
						</div>
					)}
				</section>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="danger" onClick={rest.onHide}>
					Fechar
				</Button>
				<Button
					disabled={
						selectedOptionState?.value && selectedOptionAccessType === "uf"
							? false
							: isDisabledRegisterButton
					}
					variant="success"
					onClick={handleRegister}
				>
					Confimar
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
